import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";
import { debugLog, debugError } from "@/lib/debug";

interface NavigationBlockerContextType {
  isBlocked: boolean;
  blockNavigation: (callback: () => Promise<boolean>) => void;
  unblockNavigation: () => void;
  checkNavigation: () => Promise<boolean>;
}

const NavigationBlockerContext = createContext<NavigationBlockerContextType | undefined>(undefined);

interface NavigationBlockerProviderProps {
  children: ReactNode;
}

export const NavigationBlockerProvider = ({ children }: NavigationBlockerProviderProps) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockCallback, setBlockCallback] = useState<(() => Promise<boolean>) | null>(null);

  const blockNavigation = useCallback((callback: () => Promise<boolean>) => {
    debugLog("NavigationBlocker", "Блокируем навигацию");
    setIsBlocked(true);
    setBlockCallback(() => callback);
  }, []);

  const unblockNavigation = useCallback(() => {
    debugLog("NavigationBlocker", "Разблокируем навигацию");
    setIsBlocked(false);
    setBlockCallback(null);
  }, []);

  const checkNavigation = useCallback(async (): Promise<boolean> => {
    debugLog("NavigationBlocker", "Проверяем навигацию. isBlocked:", isBlocked, "hasCallback:", !!blockCallback);
    console.log("[NavigationBlocker] Детали состояния - isBlocked:", isBlocked, "hasCallback:", !!blockCallback);

    if (!isBlocked || !blockCallback) {
      console.log("[NavigationBlocker] Навигация разрешена (не заблокировано или нет callback)");
      return true;
    }

    try {
      console.log("[NavigationBlocker] Вызываем callback для проверки навигации");
      const result = await blockCallback();
      debugLog("NavigationBlocker", "Результат проверки навигации:", result);
      console.log("[NavigationBlocker] Результат callback:", result);

      // Если навигация разрешена, автоматически разблокируем
      if (result) {
        console.log("[NavigationBlocker] Навигация разрешена, автоматически разблокируем");
        setIsBlocked(false);
        setBlockCallback(null);
      }

      return result;
    } catch (error) {
      debugError("NavigationBlocker", "Ошибка при проверке навигации:", error);
      console.error("[NavigationBlocker] Ошибка при проверке навигации:", error);
      // В случае ошибки разблокируем навигацию и разрешаем переход
      setIsBlocked(false);
      setBlockCallback(null);
      return true;
    }
  }, [isBlocked, blockCallback]);

  const value = useMemo(
    () => ({
      isBlocked,
      blockNavigation,
      unblockNavigation,
      checkNavigation,
    }),
    [isBlocked, blockNavigation, unblockNavigation, checkNavigation]
  );

  return <NavigationBlockerContext.Provider value={value}>{children}</NavigationBlockerContext.Provider>;
};

export const useNavigationBlocker = () => {
  const context = useContext(NavigationBlockerContext);
  if (context === undefined) {
    throw new Error("useNavigationBlocker must be used within a NavigationBlockerProvider");
  }
  return context;
};
